
import { Link } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';

const Palmoplantar = () => {
  const symptoms = [
    '手のひらや足の裏に膿疱',
    '赤い炎症を伴う',
    'かゆみや痛みがある',
    '皮膚が厚くなる',
    '爪の変形',
    'ストレスで悪化する'
  ];

  const causes = [
    {
      title: '免疫異常',
      description: '自己免疫システムの異常',
      icon: 'ri-shield-cross-line'
    },
    {
      title: 'ストレス',
      description: '精神的・身体的ストレス',
      icon: 'ri-emotion-unhappy-line'
    },
    {
      title: '金属アレルギー',
      description: '歯科金属などのアレルギー',
      icon: 'ri-hospital-line'
    },
    {
      title: '慢性感染',
      description: '扁桃腺炎などの慢性感染症',
      icon: 'ri-virus-line'
    }
  ];

  const treatments = [
    {
      category: '漢方薬',
      items: [
        { name: '黄連解毒湯', effect: '熱を冷まし炎症を鎮める' },
        { name: '十味敗毒湯', effect: '化膿を抑え解毒する' },
        { name: '桂枝茯苓丸', effect: '血の巡りを改善する' }
      ]
    },
    {
      category: '生薬',
      items: [
        { name: '黄連', effect: '清熱解毒・炎症を鎮める' },
        { name: '連翹', effect: '化膿性炎症を改善' },
        { name: '当帰', effect: '血を補い皮膚に潤いを与える' }
      ]
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    "name": "掌蹠膿疱症の漢方治療 - 一心堂薬局",
    "description": "掌蹠膿疱症の症状、原因、漢方での治療法について詳しく解説。一心堂薬局では体質改善による根本的な治療を行います。",
    "url": `${import.meta.env.VITE_SITE_URL}/palmoplantar-pustulosis`,
    "mainEntity": {
      "@type": "MedicalCondition",
      "name": "掌蹠膿疱症",
      "alternateName": "しょうせきのうほうしょう",
      "description": "手のひらや足の裏に膿疱が繰り返し現れる慢性の皮膚疾患",
      "possibleCause": [
        "免疫異常",
        "ストレス",
        "金属アレルギー",
        "慢性感染"
      ],
      "signOrSymptom": [
        "手のひらや足の裏に膿疱",
        "赤い炎症を伴う",
        "かゆみや痛みがある",
        "皮膚が厚くなる"
      ]
    },
    "about": {
      "@type": "MedicalBusiness",
      "name": "一心堂薬局",
      "telephone": "0479-70-6065"
    },
    "specialty": "漢方薬学",
    "medicalAudience": {
      "@type": "Patient"
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 to-white">
      <SEOHead
        title="掌蹠膿疱症の漢方治療 - 一心堂薬局"
        description="掌蹠膿疱症の症状、原因、漢方での治療法について詳しく解説。一心堂薬局では体質改善による根本的な治療を行います。"
        keywords="掌蹠膿疱症,膿疱症,漢方治療,一心堂薬局,多古町,千葉県,皮膚疾患,手のひら,足の裏"
        canonicalUrl={`${import.meta.env.VITE_SITE_URL}/palmoplantar-pustulosis`}
        ogTitle="掌蹠膿疱症の漢方治療 - 一心堂薬局"
        ogDescription="掌蹠膿疱症の症状、原因、漢方での治療法について詳しく解説。体質改善による根本的な治療を行います。"
        structuredData={structuredData}
      />
      
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-sm shadow-sm z-50">
        <div className="max-w-md mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <i className="ri-arrow-left-line text-green-800 text-xl"></i>
              <span className="text-green-800 font-medium">戻る</span>
            </Link>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center">
                <i className="ri-leaf-fill text-white text-sm"></i>
              </div>
              <span className="font-bold text-green-800" style={{fontFamily: 'Pacifico, serif'}}>一心堂薬局</span>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="pt-20 pb-8 px-4">
        <div className="max-w-md mx-auto text-center">
          <div className="mb-6">
            <img 
              src="https://public.readdy.ai/ai/img_res/a79f030cc602bf76a64ed453294ab56d.jpg"
              alt="掌蹠膿疱症"
              className="w-full h-48 object-cover rounded-2xl shadow-lg"
            />
          </div>
          <h1 className="text-2xl font-bold text-pink-800 mb-2">
            掌蹠膿疱症<br />
            <span className="text-lg">(しょうせきのうほうしょう)</span>
          </h1>
          <p className="text-pink-600 text-lg mb-4">
            漢方で体質改善
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-6">
            掌蹠膿疱症は、手のひらや足の裏に膿疱（うみを持った水ぶくれ）が繰り返しできる慢性的な皮膚疾患です。かゆみや痛みを伴い、日常生活に支障をきたすことがあります。扁桃炎や歯周病などの感染症、金属アレルギー、喫煙などが関連していると考えられています。
          </p>
        </div>
      </div>

      {/* Symptoms Section */}
      <div className="px-4 py-8 bg-pink-50">
        <div className="max-w-md mx-auto">
          <h2 className="text-xl font-bold text-pink-800 mb-4 text-center">
            主な症状
          </h2>
          <div className="grid grid-cols-2 gap-3">
            {symptoms.map((symptom, index) => (
              <div key={index} className="bg-white py-3 px-4 rounded-xl text-center text-sm font-medium text-pink-800 border border-pink-100">
                {symptom}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Causes Section */}
      <div className="px-4 py-8 bg-white">
        <div className="max-w-md mx-auto">
          <h2 className="text-xl font-bold text-pink-800 mb-6 text-center">
            主な原因
          </h2>
          <div className="space-y-4">
            {causes.map((cause, index) => (
              <div key={index} className="bg-pink-50 rounded-2xl p-4 border border-pink-100">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-pink-100 rounded-full flex items-center justify-center">
                    <i className={`${cause.icon} text-pink-600 text-lg`}></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-pink-800">{cause.title}</h3>
                    <p className="text-sm text-gray-600">{cause.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Treatment Section */}
      <div className="px-4 py-8 bg-pink-50">
        <div className="max-w-md mx-auto">
          <h2 className="text-xl font-bold text-pink-800 mb-6 text-center">
            漢方での改善方法
          </h2>
          <div className="space-y-6">
            {treatments.map((treatment, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-sm">
                <h3 className="text-lg font-bold text-pink-800 mb-4">{treatment.category}</h3>
                <div className="space-y-3">
                  {treatment.items.map((item, itemIndex) => (
                    <div key={itemIndex} className="border-l-4 border-pink-200 pl-4">
                      <h4 className="font-semibold text-pink-700">{item.name}</h4>
                      <p className="text-sm text-gray-600">{item.effect}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Consultation Section */}
      <div className="px-4 py-8 bg-white">
        <div className="max-w-md mx-auto text-center">
          <div className="bg-gradient-to-r from-pink-100 to-pink-50 rounded-2xl p-6">
            <h2 className="text-xl font-bold text-pink-800 mb-4">
              お気軽にご相談ください
            </h2>
            <p className="text-sm text-gray-700 mb-6 leading-relaxed">
              掌蹠膿疱症は慢性的な疾患ですが、適切な漢方薬と生活習慣の改善で症状の軽減が期待できます。体質や症状に合わせて最適な処方をご提案いたします。
            </p>
            <div className="flex space-x-3">
              <a 
                href="tel:0479-70-6065"
                className="flex-1 bg-pink-600 text-white py-3 px-4 rounded-full font-medium text-center"
              >
                <i className="ri-phone-line mr-2"></i>
                お電話
              </a>
              <Link 
                to="/"
                className="flex-1 bg-white text-pink-600 py-3 px-4 rounded-full font-medium border border-pink-200 text-center"
              >
                <i className="ri-home-line mr-2"></i>
                ホームへ
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 w-full bg-white border-t border-gray-200 z-50">
        <div className="max-w-md mx-auto">
          <div className="grid grid-cols-5 py-2">
            <Link to="/" className="flex flex-col items-center justify-center py-2 text-gray-400">
              <i className="ri-home-5-line text-lg"></i>
              <span className="text-xs mt-1">ホーム</span>
            </Link>
            <button className="flex flex-col items-center justify-center py-2 text-gray-400">
              <i className="ri-medicine-bottle-line text-lg"></i>
              <span className="text-xs mt-1">業務内容</span>
            </button>
            <button className="flex flex-col items-center justify-center py-2 text-gray-400">
              <i className="ri-leaf-line text-lg"></i>
              <span className="text-xs mt-1">漢方相談</span>
            </button>
            <button className="flex flex-col items-center justify-center py-2 text-gray-400">
              <i className="ri-map-pin-line text-lg"></i>
              <span className="text-xs mt-1">アクセス</span>
            </button>
            <button className="flex flex-col items-center justify-center py-2 text-pink-600">
              <i className="ri-hand-heart-fill text-lg"></i>
              <span className="text-xs mt-1">掌蹠膿疱症</span>
            </button>
          </div>
        </div>
      </nav>

      <div className="h-20"></div>
    </div>
  );
};

export default Palmoplantar;
